// src/pages/Register.jsx
// Production-level Register Page — TrueChattr
// Fixes: "AI" → "keyword automation", "14-day trial" removed (free plan forever),
//        "500+ businesses" removed (early access), right panel honest copy
// Upgrades: polished micro-animations, social proof ticker, floating feature cards,
//           real-time validation UX, better visual hierarchy

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Lock, Building2, Loader2, MessageSquare,
  ArrowRight, CheckCircle2, Shield, Zap, Users,
  Eye, EyeOff, Sparkles, Bot, BarChart3, Send,
} from 'lucide-react';
import { Input }        from '@/components/ui/input';
import { Label }        from '@/components/ui/label';
import api              from '@/lib/api';
import useAuthStore     from '@/store/useAuthStore';
import { toast }        from 'sonner';
import { Helmet }       from 'react-helmet-async';

// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANEL CONTENT — honest, no AI claims
// ─────────────────────────────────────────────────────────────────────────────

const BENEFITS = [
  'Setup in under 5 minutes — no coding needed',
  'Keyword bot handles customer queries 24/7',
  'Capture & nurture leads from WhatsApp',
  'Broadcast promotions to thousands instantly',
];

const STATS = [
  { icon: Zap,         value: '24/7',  label: 'Auto Reply'  },
  { icon: Users,       value: '3×',    label: 'More Leads'  },
  { icon: BarChart3,   value: '80%',   label: 'Time Saved'  },
];

// Floating feature badges shown on right panel
const FEATURE_BADGES = [
  { icon: Bot,          label: 'Keyword Bot Live',        sub: '24/7 auto-reply active',  color: 'from-emerald-400/20 to-teal-400/20',   border: 'border-emerald-300/30', top: '18%',  right: '-5%'  },
  { icon: Send,         label: '1,247 msgs sent',         sub: 'Bulk broadcast completed', color: 'from-blue-400/20 to-indigo-400/20',    border: 'border-blue-300/30',    top: '52%',  right: '-8%'  },
  { icon: BarChart3,    label: '98% open rate',           sub: 'WhatsApp vs email',        color: 'from-violet-400/20 to-purple-400/20',  border: 'border-violet-300/30',  top: '75%',  right: '-3%'  },
];

// Social proof ticker messages
const TICKER = [
  '🚀 Rahul from Delhi just signed up',
  '⚡ Priya set up her keyword bot',
  '📊 Amit sent bulk broadcast to 800 customers',
  '✅ Neha captured 12 new leads today',
  '🎯 Ravi automated his FAQ replies',
];

// ─────────────────────────────────────────────────────────────────────────────
// PASSWORD STRENGTH
// ─────────────────────────────────────────────────────────────────────────────

const getStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 6)           score++;
  if (pw.length >= 10)          score++;
  if (/[A-Z]/.test(pw))        score++;
  if (/[0-9]/.test(pw))        score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Weak',   color: 'bg-rose-400'   };
  if (score <= 3) return { score, label: 'Fair',   color: 'bg-amber-400'  };
  return           { score, label: 'Strong', color: 'bg-emerald-500' };
};

// ─────────────────────────────────────────────────────────────────────────────
// SOCIAL PROOF TICKER
// ─────────────────────────────────────────────────────────────────────────────

function SocialTicker() {
  const [idx, setIdx] = useState(0);

  useState(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % TICKER.length), 3000);
    return () => clearInterval(t);
  });

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-100 overflow-hidden">
      <span className="relative flex h-2 w-2 flex-shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
      </span>
      <AnimatePresence mode="wait">
        <motion.p
          key={idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
          className="text-xs font-medium text-emerald-700 truncate"
        >
          {TICKER[idx]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Register() {
  const [businessName,  setBusinessName]  = useState('');
  const [email,         setEmail]         = useState('');
  const [password,      setPassword]      = useState('');
  const [showPassword,  setShowPassword]  = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [touched,       setTouched]       = useState({});

  const navigate = useNavigate();
  const setAuth  = useAuthStore(s => s.setAuth);
  const strength = getStrength(password);

  // Field-level validation
  const errors = {
    businessName: touched.businessName && businessName.trim().length < 2
      ? 'At least 2 characters required' : '',
    email: touched.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      ? 'Enter a valid email address' : '',
    password: touched.password && password.length < 6
      ? 'Minimum 6 characters' : '',
  };

  const isFormValid =
    businessName.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    password.length >= 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ businessName: true, email: true, password: true });

    if (!isFormValid) return;

    setLoading(true);
    try {
      const { data } = await api.post('/auth/signup', {
        email:        email.toLowerCase().trim(),
        password,
        businessName: businessName.trim(),
      });

      const { token, user } = data;
      if (!token || !user) throw new Error('Invalid response from server');

      setAuth(user, token);
      toast.success('Account created successfully! 🎉');
      navigate('/onboarding');
    } catch (err) {
      toast.error(
        err.response?.data?.error || err.message || 'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <Helmet>
        <title>Sign Up Free — TrueChattr | WhatsApp Automation</title>
        <meta name="description" content="Create your free TrueChattr account and start automating WhatsApp with keyword replies, bulk messaging, and lead management. No credit card required." />
        <link rel="canonical" href="https://truechattr.in/register" />
        <meta property="og:title" content="Sign Up Free — TrueChattr" />
        <meta property="og:description" content="Automate WhatsApp with keyword replies, bulk messaging, and lead capture. Free account, no credit card required." />
        <meta property="og:url" content="https://truechattr.in/register" />
      </Helmet>

      {/* ══ LEFT — Form Panel ════════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, x: -32 }}
        animate={{ opacity: 1, x: 0   }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16 bg-white"
      >
        <div className="w-full max-w-sm space-y-6">

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0   }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="flex items-center gap-3"
          >
            <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-200">
              <MessageSquare className="w-5 h-5 text-white" />
              <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900 leading-none">TrueChattr</p>
              <p className="text-xs text-gray-400 mt-0.5">WhatsApp Automation</p>
            </div>
          </motion.div>

          {/* Social proof ticker */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <SocialTicker />
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Create account</h1>
            <p className="mt-1.5 text-sm text-gray-400">
              Free forever — no credit card required.
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="register-form"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0  }}
            transition={{ delay: 0.3, duration: 0.45 }}
          >
            {/* Business Name */}
            <div className="space-y-1.5">
              <Label htmlFor="businessName" className="text-sm font-medium text-gray-700">
                Business Name <span className="text-rose-400">*</span>
              </Label>
              <div className="relative">
                <Building2 className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                  errors.businessName ? 'text-rose-400' : businessName.trim().length >= 2 ? 'text-emerald-500' : 'text-gray-400'
                }`} />
                <Input
                  id="businessName"
                  type="text"
                  placeholder="My Awesome Store"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, businessName: true }))}
                  className={`pl-10 h-11 rounded-xl text-sm transition-all ${
                    errors.businessName
                      ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400'
                      : businessName.trim().length >= 2
                        ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                        : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-400'
                  }`}
                  data-testid="register-business-input"
                  required minLength={2} maxLength={100}
                  disabled={loading}
                />
                {businessName.trim().length >= 2 && !errors.businessName && (
                  <CheckCircle2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none" />
                )}
              </div>
              <AnimatePresence>
                {errors.businessName && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                    className="text-xs text-rose-500 font-medium"
                  >
                    {errors.businessName}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email address <span className="text-rose-400">*</span>
              </Label>
              <div className="relative">
                <Mail className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                  errors.email ? 'text-rose-400' : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'text-emerald-500' : 'text-gray-400'
                }`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, email: true }))}
                  className={`pl-10 h-11 rounded-xl text-sm transition-all ${
                    errors.email
                      ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400'
                      : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                        ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                        : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-400'
                  }`}
                  data-testid="register-email-input"
                  required disabled={loading}
                  autoComplete="email"
                />
                {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !errors.email && (
                  <CheckCircle2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none" />
                )}
              </div>
              <AnimatePresence>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                    className="text-xs text-rose-500 font-medium"
                  >
                    {errors.email}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password <span className="text-rose-400">*</span>
              </Label>
              <div className="relative">
                <Lock className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-colors ${
                  errors.password ? 'text-rose-400' : password.length >= 6 ? 'text-emerald-500' : 'text-gray-400'
                }`} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, password: true }))}
                  className={`pl-10 pr-10 h-11 rounded-xl text-sm transition-all ${
                    errors.password
                      ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-400'
                      : password.length >= 6
                        ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                        : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-400'
                  }`}
                  data-testid="register-password-input"
                  required minLength={6} disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password strength */}
              <AnimatePresence>
                {password && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-1.5 pt-1"
                  >
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div
                          key={i}
                          className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                            i <= strength.score ? strength.color : 'bg-gray-100'
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs font-semibold ${
                      strength.label === 'Strong' ? 'text-emerald-600'
                      : strength.label === 'Fair'  ? 'text-amber-500'
                      : 'text-rose-500'
                    }`}>
                      {strength.label} password
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                    className="text-xs text-rose-500 font-medium"
                  >
                    {errors.password}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              whileTap={{ scale: 0.98 }}
              className={`
                w-full flex items-center justify-center gap-2
                h-11 rounded-xl font-semibold text-sm text-white
                bg-gradient-to-r from-emerald-500 to-emerald-600
                hover:from-emerald-600 hover:to-emerald-700
                shadow-lg shadow-emerald-200/60
                disabled:opacity-60 disabled:cursor-not-allowed
                transition-all duration-200 mt-2
              `}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account…
                </>
              ) : (
                <>
                  Create Free Account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
          </motion.form>

          {/* Divider */}
          <motion.div
            className="relative"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.3 }}
          >
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400">or continue with</span>
            </div>
          </motion.div>

          {/* Google Button */}
          <motion.button
            type="button"
            onClick={() => window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.35 }}
            className="w-full flex items-center justify-center gap-3 h-11 rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 text-sm font-semibold text-gray-700 transition-all duration-200"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
            Continue with Google
          </motion.button>

          {/* Sign in link */}
          <motion.p
            className="text-center text-sm text-gray-400"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.3 }}
          >
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-emerald-600 hover:text-emerald-700 font-semibold"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </motion.p>

          {/* Trust line — honest: no "14-day trial" */}
          <motion.p
            className="text-center text-xs text-gray-300"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.3 }}
          >
            🔒 No credit card required · Free plan forever · Cancel anytime
          </motion.p>

        </div>
      </motion.div>

      {/* ══ RIGHT — Visual Panel ═════════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, x: 32 }}
        animate={{ opacity: 1, x: 0  }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="hidden lg:flex flex-1 relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-700 items-center justify-center p-12"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-600/20 rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
        </div>

        {/* Floating badge cards */}
        {FEATURE_BADGES.map((badge, i) => {
          const Icon = badge.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0,  scale: 1   }}
              transition={{ delay: 0.7 + i * 0.15, duration: 0.5 }}
              style={{ top: badge.top, right: badge.right }}
              className={`absolute backdrop-blur-md bg-gradient-to-br ${badge.color} border ${badge.border} rounded-2xl px-4 py-3 shadow-xl min-w-[180px]`}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold leading-tight">{badge.label}</p>
                  <p className="text-white/70 text-[10px] mt-0.5">{badge.sub}</p>
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* Main content */}
        <div className="relative z-10 max-w-sm space-y-8">

          {/* Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1,   opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-xl"
          >
            <MessageSquare className="w-8 h-8 text-white" />
          </motion.div>

          {/* Headline — honest, no "500+ businesses" */}
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/90 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Now in Early Access — Limited Spots
            </div>
            <h2 className="text-3xl font-bold text-white leading-snug">
              Automate Your WhatsApp.<br />
              <span className="text-emerald-200">Focus on Growth.</span>
            </h2>
            <p className="text-emerald-100 text-sm leading-relaxed">
              Set keywords once — your bot handles customer queries, product questions, and lead capture automatically, 24/7.
            </p>
          </div>

          {/* Benefits list */}
          <ul className="space-y-3">
            {BENEFITS.map((b, i) => (
              <motion.li
                key={b}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0  }}
                transition={{ delay: 0.5 + i * 0.08, duration: 0.35 }}
                className="flex items-center gap-3"
              >
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm text-emerald-50">{b}</span>
              </motion.li>
            ))}
          </ul>

          {/* Stats row */}
          <div className="flex gap-3">
            {STATS.map(({ icon: Icon, value, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0  }}
                transition={{ delay: 0.7 + i * 0.1, duration: 0.35 }}
                className="flex-1 flex flex-col items-center gap-1 px-3 py-3 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20"
              >
                <Icon className="w-4 h-4 text-emerald-200 mb-0.5" />
                <span className="text-xl font-bold text-white leading-none">{value}</span>
                <span className="text-xs text-emerald-200 text-center">{label}</span>
              </motion.div>
            ))}
          </div>

          {/* Bottom trust note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.4 }}
            className="flex items-center gap-2 pt-2 border-t border-white/20"
          >
            <Shield className="w-4 h-4 text-emerald-200 flex-shrink-0" />
            <p className="text-xs text-emerald-200 leading-relaxed">
              Uses Official WhatsApp Business API — fully compliant with WhatsApp policies
            </p>
          </motion.div>

        </div>
      </motion.div>

    </div>
  );
}
